import { Editor, Transforms, Path } from 'slate';
import { ReactEditor } from 'slate-react';

function clearAiThinkingFromAllBlocks(editor) {
  for (const [node, path] of Editor.nodes(editor, {
    at: [],
    match: function(n) {
      return n.properties != null && n.properties.aiThinking === true;
    }
  })) {
    const newProperties = Object.assign({}, node.properties);
    delete newProperties.aiThinking;
    delete newProperties.aiThinkingStartedAt;
    Transforms.setNodes(editor, { properties: newProperties }, { at: path });
  }
}

function focusInsertedBlocks(editor, insertPath, count) {
  const lastIndex = insertPath[insertPath.length - 1] + count - 1;
  const lastPath = [...insertPath.slice(0, -1), lastIndex];
  const anchor = Editor.start(editor, insertPath);
  const focus = Editor.end(editor, lastPath);
  Transforms.select(editor, { anchor, focus });
  Transforms.collapse(editor);
  // ReactEditor.focus(editor);
}

function assertSameParentRange(start, end) {

  const startParent = start.slice(0, -1);
  const endParent = end.slice(0, -1);

  if (Path.equals(startParent, endParent) === false) {
    throw new Error('range.start and range.end must be sibling paths under the same parent');
  }

  const startIndex = start[start.length - 1];
  const endIndex = end[end.length - 1];

  if (endIndex < startIndex) {
    throw new Error('range.end must be greater than or equal to range.start');
  }

  return {
    parentPath: startParent,
    startIndex,
    endIndex
  };

}

export function applyPageBlockPatch(editor, data) {

  Editor.withoutNormalizing(editor, function applyPatch() {

    if (data.operation === 'insert') {

      if (data.blocks.length > 0) {
        Transforms.insertNodes(editor, data.blocks, { at: data.path });
      }

      return;

    }

    if (data.operation === 'replace') {

      const rangeDetails = assertSameParentRange(data.range.start, data.range.end);
      const parentPath = rangeDetails.parentPath;
      const startIndex = rangeDetails.startIndex;
      const endIndex = rangeDetails.endIndex;

      for (let i = endIndex - 1; i >= startIndex; i--) {
        Transforms.removeNodes(editor, { at: [...parentPath, i] });
      }

      if (data.blocks.length > 0) {
        Transforms.insertNodes(editor, data.blocks, { at: data.range.start });
      }

      return;

    }

    if (data.operation === 'delete') {

      if (data.path != null) {
        Transforms.removeNodes(editor, { at: data.path });
        return;
      }

      const rangeDetails = assertSameParentRange(data.range.start, data.range.end);
      const parentPath = rangeDetails.parentPath;
      const startIndex = rangeDetails.startIndex;
      const endIndex = rangeDetails.endIndex;

      for (let i = endIndex - 1; i >= startIndex; i--) {
        Transforms.removeNodes(editor, { at: [...parentPath, i] });
      }

      return;

    }

    throw new Error('Unsupported operation: ' + data.operation);

  });

  clearAiThinkingFromAllBlocks(editor);

  if (data.operation === 'insert' && data.blocks.length > 0) {
    focusInsertedBlocks(editor, data.path, data.blocks.length);
  }

  if (data.operation === 'replace' && data.blocks.length > 0) {
    focusInsertedBlocks(editor, data.range.start, data.blocks.length);
  }

  scrollSlateSelectionIntoView(editor, 'smooth');

}

export function applyFullContentUpdate(editor, content) {

  Editor.withoutNormalizing(editor, function replaceAll() {

    const currentChildrenLength = editor.children.length;
    const newChildrenLength = content.length;

    for (let index = 0; index < newChildrenLength; index++) {
      Transforms.insertNodes(editor, content[index], { at: [currentChildrenLength + index] });
    }

    for (let index = 0; index < currentChildrenLength; index++) {
      Transforms.removeNodes(editor, { at: [0] });
    }

  });

  clearAiThinkingFromAllBlocks(editor);

}

export function applyAiThinkingStarted(editor, data) {

  const aiThinkingStartedAt = Date.now();

  Editor.withoutNormalizing(editor, function applyThinking() {

    if (data.operation === 'insert') {
      if (Editor.hasPath(editor, data.path) === false) {
        return;
      }
      const node = Editor.node(editor, data.path)[0];
      Transforms.setNodes(editor, {
        properties: {
          ...node.properties,
          aiThinking: true,
          aiThinkingStartedAt
        }
      }, { at: data.path });
      return;
    }

    if (data.operation === 'replace') {
      const rangeDetails = assertSameParentRange(data.range.start, data.range.end);
      const parentPath = rangeDetails.parentPath;
      const startIndex = rangeDetails.startIndex;
      const endIndex = rangeDetails.endIndex;

      for (let i = startIndex; i < endIndex; i++) {
        const path = [...parentPath, i];
        const node = Editor.node(editor, path)[0];
        Transforms.setNodes(editor, {
          properties: {
            ...node.properties,
            aiThinking: true,
            aiThinkingStartedAt
          }
        }, { at: path });
      }
    }

  });

  if (data.operation === 'insert' && Editor.hasPath(editor, data.path)) {
    focusInsertedBlocks(editor, data.path, 1);
    scrollSlateSelectionIntoView(editor, 'smooth');
  }

  if (data.operation === 'replace') {
    const rangeDetails = assertSameParentRange(data.range.start, data.range.end);
    const count = rangeDetails.endIndex - rangeDetails.startIndex;
    if (count > 0) {
      focusInsertedBlocks(editor, data.range.start, count);
      scrollSlateSelectionIntoView(editor, 'smooth');
    }
  }

}

export function scrollSlateSelectionIntoView(
  editor,
  animation
) {
  if (!editor.selection) return false;

  requestAnimationFrame(() => {
    try {
      const domRange = ReactEditor.toDOMRange(editor, editor.selection);
      const rect = getRangeRect(domRange);
      if (!rect) return;

      const element = getRangeStartElement(domRange);
      if (!element) return;

      const scrollParent = getScrollParent(element);

      if (scrollParent === window) {
        const targetTop =
          window.scrollY +
          rect.top -
          window.innerHeight / 2 +
          rect.height / 2;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: animation,
        });

        return;
      }

      const container = scrollParent;
      const containerRect = container.getBoundingClientRect();

      const targetTop =
        container.scrollTop +
        rect.top -
        containerRect.top -
        container.clientHeight / 2 +
        rect.height / 2;

      container.scrollTo({
        top: Math.max(0, targetTop),
        behavior: animation,
      });
    } catch {
      // Selection may be stale or DOM may not be mounted yet.
    }
  });

  return true;
}

function getRangeRect(range) {
  const rects = range.getClientRects();

  if (rects.length > 0) {
    return rects[0];
  }

  const rect = range.getBoundingClientRect();

  if (rect) {
    return rect;
  }

  return null;
}

function getRangeStartElement(range) {
  const node = range.startContainer;

  if (node.nodeType === Node.ELEMENT_NODE) {
    return node;
  }

  return node.parentElement;
}

function getScrollParent(element) {
  let current = element.parentElement;

  while (current) {
    const style = window.getComputedStyle(current);

    const canScroll =
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight;

    if (canScroll) {
      return current;
    }

    current = current.parentElement;
  }

  return window;
}

window.scrollSlateSelectionIntoView = scrollSlateSelectionIntoView;